import { useCustomToast } from '@elkaso-app/web-design';
import http from 'apis/config/http-service';
import { useMutationWrapper } from 'apis/config/use-api-wrapper';
import { AxiosError } from 'axios';
import { useQueryClient } from 'react-query';
import { getCrmBrandTradeLicenseMappingsEndpointIdentifier } from './use-get-crm-brand-trade-license-mappings-api';

interface UpdateMappingBrandPayload {
  brandId?: number;
  brandConfidence?: number;
}

interface UpdateMappingVariables {
  id: number;
  data: UpdateMappingBrandPayload;
}

export function useUpdateMappingBrandApi(options?: any) {
  const toast = useCustomToast();
  const queryClient = useQueryClient();

  const mutationFn = async ({ id, data }: UpdateMappingVariables): Promise<void> => {
    try {
      const response = await http.put(`ums/v1/crm/management/brands/trade-license/mappings/${id}/brand`, data);
      return response.data;
    } catch (error: any) {
      if (!error.response?.status || !error.response?.data?.message) throw error;

      toast({
        title: 'Error Updating Brand Mapping',
        description:
          error.response.data.message || 'Failed to update brand mapping. Please check your data and try again.',
        status: 'error',
        duration: 7000,
        isClosable: true,
      });

      throw error;
    }
  };

  return useMutationWrapper<void, AxiosError, UpdateMappingVariables>(mutationFn, {
    onSuccess: (_data) => {
      toast({
        title: 'Brand Mapping Updated Successfully!',
        description: `Trade license brand mapping has been updated with new confidence score.`,
        status: 'success',
        duration: 5000,
        isClosable: true,
      });

      // Invalidate all mapping queries to force refetch
      queryClient.invalidateQueries([getCrmBrandTradeLicenseMappingsEndpointIdentifier]);
    },
    onError: () => {
      toast({
        title: 'Update Failed',
        description: 'Something went wrong while updating the brand mapping. Please try again.',
        status: 'error',
        duration: 7000,
        isClosable: true,
      });
    },
    ...options,
  });
}
